import { css } from "@emotion/react";
import { confirmDialog } from "primereact/confirmdialog";
import {
  IconChannelBot,
  whatsapp,
  facebookDataTable,
  InstagramIcon,
  spaik
} from "../../assets";
import { Button, useToast } from "../../components";
import useOmnichannel, { iDialogMessagesFile } from "../use-omnichannel";
import Avatar from "../avatar";
import { Tooltip } from "primereact/tooltip";
import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  setDialogActive,
  toggleTransferModal
} from "../../redux/slices/flowInteractions";
import { setAllDialogs, setSidebarChats } from "../../redux/slices/sidebar";
import { useEffect, useState } from "react";
import { AuthContextType, useAuth } from "../../auth/auth-provider";
import { setRefreshAllDialogs } from "../../redux/slices/omnichannel";

export interface TopbarProps {
  dialog: iDialogMessagesFile;
  onFinalize: () => void;
  onOpenContactInfos?: () => void;
}

const Topbar = ({ dialog, onFinalize, onOpenContactInfos }: TopbarProps) => {
  const { finalize } = useOmnichannel();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toastRef = useToast();
  const [screenMobile, setScreenMobile] = useState(false);
  const allDialogs = useAppSelector((state) => state.sidebar.allDialogs);
  const { hasAnyPermission } = useAuth() as AuthContextType;
  const contactId = useAppSelector((state) => state.omnichannel.contactId);
  const nameContact = useAppSelector((state) => state.omnichannel.nameContact);
  const selectedContactAvatarUrl = useAppSelector(
    (state) => state.omnichannel.selectedContactAvatarUrl
  );

  const options: { [key: string]: string } = {
    LIVECHAT: spaik,
    WHATSAPP: whatsapp,
    FACEBOOK: facebookDataTable,
    INSTAGRAM: InstagramIcon
  };
  const handleOpenWhatsapp = () => {
    window.open(`https://wa.me/${dialog.phone}`);
  };
  useEffect(() => {
    dispatch(setDialogActive(dialog.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog.id]);
  const platformImage: string | undefined = options[dialog.platform];
  const handleOpennModal = () => {
    if (dialog.transferredToThirdpartySystem) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Este chamado já foi transferido para um sistema de terceiros"
      });
    } else {
      dispatch(toggleTransferModal());
    }
  };

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 500) {
        setScreenMobile(true);
      } else {
        setScreenMobile(false);
      }
    }
    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const contactName = nameContact !== "" ? nameContact : dialog.name;
  const avatarUrl =
    contactId > 0 ? selectedContactAvatarUrl : dialog.avatarUrl;

  return (
    <div
      css={css`
        display: flex;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
          ${screenMobile && `padding: 0.5rem`}
        `}
      >
        {screenMobile && (
          <Button
            label="<"
            className="p-button-info"
            onClick={() => dispatch(setSidebarChats(true))}
          />
        )}
      </div>
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e5e5e5;
          width: 100%;

          @media screen and (max-width: 599px) {
            gap: 1rem;
            flex-direction: column;
            padding: 12px;
          }
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <div
            role={onOpenContactInfos ? "button" : undefined}
            tabIndex={onOpenContactInfos ? 0 : undefined}
            onClick={onOpenContactInfos}
            onKeyDown={(event) => {
              if (
                onOpenContactInfos &&
                (event.key === "Enter" || event.key === " ")
              ) {
                event.preventDefault();
                onOpenContactInfos();
              }
            }}
            css={css`
              position: relative;
              cursor: ${onOpenContactInfos ? "pointer" : "default"};
            `}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={contactName}
                css={css`
                  height: 42px;
                  width: 42px;
                  border-radius: 50%;
                  object-fit: cover;
                  margin-right: 6px;
                `}
              />
            ) : (
              <Avatar name={contactName} />
            )}

            <div
              className="bot-name-tooltip"
              data-pr-position="bottom"
              css={css`
                display: flex;
                align-items: center;
                position: absolute;
                bottom: 0;
                right: 5px;
                gap: 10px;
              `}
            >
              <img
                src={platformImage}
                height={15}
                width={platformImage === spaik ? 20 : 15}
              />

              <Tooltip target=".bot-name-tooltip"> {dialog.platform} </Tooltip>
            </div>
          </div>

          <>
            <div>
              <div>{contactName}</div>
              {contactId < 1 && (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    font-size: 12px;
                    color: #90a0b7;
                  `}
                >
                  {dialog.botName} | Status: {dialog.status} | Sala: #
                  {dialog.id}
                </div>
              )}
            </div>
          </>
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;

            @media screen and (max-width: 599px) {
              gap: 1rem;
            }
          `}
        >
          {contactId < 1 && (
            <>
              <Button
                className="p-button-success"
                icon="pi pi-whatsapp"
                tooltip="Falar via Whatsapp"
                tooltipOptions={{ position: "bottom" }}
                onClick={handleOpenWhatsapp}
                css={css`
                  margin-right: 7px;
                  border: none !important;
                  height: 30px !important;
                  width: 30px !important;
                  padding: 0 !important;
                  border-radius: 8px !important;
                  background: #2ed47a !important;
                `}
              />

              {hasAnyPermission("dialog_edit") ||
              hasAnyPermission("omnichannel_transfer") ? (
                <Button
                  className="p-button-secondary"
                  icon="pi pi-arrows-h"
                  tooltip="Transferir"
                  tooltipOptions={{ position: "bottom" }}
                  onClick={handleOpennModal}
                  css={css`
                    margin-right: 7px;
                    border: none !important;
                    height: 30px !important;
                    width: 30px !important;
                    padding: 0 !important;
                    border-radius: 8px !important;
                    background: #d4e2f3 !important;
                    color: #046af3 !important;
                  `}
                />
              ) : null}

              <Button
                icon="pi pi-check"
                onClick={async () => {
                  confirmDialog({
                    message: "Você deseja finalizar a conversa?",
                    header: "Atenção",
                    icon: "pi pi-exclamation-triangle",
                    accept: async () => {
                      try {
                        await finalize(dialog.id);

                        toastRef.current?.show({
                          severity: "success",
                          detail: "Conversa finalizada com sucesso."
                        });

                        const remaining = allDialogs.filter(
                          (id) => id !== dialog.id
                        );
                        dispatch(setAllDialogs(remaining));

                        dispatch(setRefreshAllDialogs(true));

                        if (remaining.length === 0) {
                          navigate({ pathname: "/rooms/" });
                        }
                      } catch (e: any) {
                        toastRef.current?.show({
                          severity: "error",
                          detail: e.description
                        });
                      }

                      onFinalize();
                    },
                    acceptLabel: "Sim",
                    rejectLabel: "Não"
                  });
                }}
                tooltip="Finalizar"
                tooltipOptions={{ position: "left" }}
                css={css`
                  border: none !important;
                  height: 30px !important;
                  width: 30px !important;
                  padding: 0 !important;
                  border-radius: 8px !important;
                `}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Topbar;
